import { STORE_BASE_CURRENCY } from "@lib/config"

export const SUPPORTED_CURRENCIES = ["UYU", "USD", "EUR", "BRL", "CLP", "ARS"] as const

export type Currency = (typeof SUPPORTED_CURRENCIES)[number]

export type ForexSnapshot = {
  base: string
  timestamp: number
  rates: Record<string, number>
  fetchedAt?: string
}

const DEFAULT_LOCALE_MAP: Record<Currency, string> = {
  UYU: "es-UY",
  USD: "en-US",
  EUR: "de-DE",
  BRL: "pt-BR",
  CLP: "es-CL",
  ARS: "es-AR",
}

const resolveFallbackCurrency = (fallback?: string | null): Currency => {
  const candidate = (fallback || STORE_BASE_CURRENCY || "USD").trim().toUpperCase()
  return (SUPPORTED_CURRENCIES as readonly string[]).includes(candidate)
    ? (candidate as Currency)
    : "USD"
}

export const normalizeCurrency = (
  value?: string | null,
  fallback?: string | null
): Currency => {
  const trimmed = value?.trim().toUpperCase() || ""
  if ((SUPPORTED_CURRENCIES as readonly string[]).includes(trimmed)) {
    return trimmed as Currency
  }
  return resolveFallbackCurrency(fallback)
}

export const toCents = (amount: number | string | null | undefined): number => {
  const numeric = typeof amount === "string" ? parseFloat(amount) : Number(amount ?? 0)
  if (!Number.isFinite(numeric)) return 0
  return Math.round(numeric * 100)
}

export const convertCents = ({
  amountCents,
  fromCurrency,
  toCurrency,
  rates,
  base = "USD",
}: {
  amountCents: number
  fromCurrency: Currency
  toCurrency: Currency
  rates: Record<string, number>
  base?: string
}): number => {
  const resolvedBase = normalizeCurrency(base, "USD")
  const resolvedFrom = normalizeCurrency(fromCurrency, resolvedBase)
  const resolvedTo = normalizeCurrency(toCurrency, resolvedBase)

  if (resolvedFrom === resolvedTo) {
    return amountCents
  }

  const resolveRate = (code: string): number | undefined => {
    const upper = code.toUpperCase()
    const direct = (rates as Record<string, unknown>)[upper] ??
      (rates as Record<string, unknown>)[upper.toLowerCase()]
    const numeric = typeof direct === "number" ? direct : Number(direct)
    return Number.isFinite(numeric) ? numeric : undefined
  }

  const rateFrom = resolvedFrom === resolvedBase ? 1 : resolveRate(resolvedFrom)
  const rateTo = resolvedTo === resolvedBase ? 1 : resolveRate(resolvedTo)

  if (!rateFrom || !rateTo) {
    return amountCents
  }

  const amountBase = amountCents / 100 / rateFrom
  const amountTo = amountBase * rateTo
  return Math.round(amountTo * 100)
}

export const formatMoney = (
  cents: number,
  currency: Currency,
  localeOverride?: string
): string => {
  const amount = Number.isFinite(cents) ? cents / 100 : 0
  const locale = localeOverride || DEFAULT_LOCALE_MAP[currency] || "en-US"

  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
    currencyDisplay: "code",
  }).format(amount)
}
